import React from "react";
import {
	Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, Radio, RadioGroup, Typography,
} from "@mui/material";
import MuiTextField from "@mui/material/TextField";
import { OTHER_SENTINEL } from "./kinds";
import { OPTION_SX } from "./WidgetFrame";
import { FIELD_SX } from "./fields/fieldProps";

const LEGEND_SX = { fontSize: 13, fontWeight: 500, color: "var(--w-text)", mb: 0.75, "&.Mui-focused": { color: "var(--w-text)" }, "&.Mui-error": { color: "var(--w-text)" } };

/**
 * A labelled list of choices — `question` (D-W15) and the `choice` field type share it. The group's
 * `legend` is announced once, each control's name is its option `label`, and the option `description`
 * is the control's accessible description (`aria-describedby`). An option image sits OUTSIDE the
 * label with `alt` = the label, so the control's name is not doubled. `allowOther` adds an Other
 * option; choosing it reveals a focused text input — the sentinel stays local (`resolveChoice`).
 */
export default function OptionList({
	id, legend, subtitle, options = [], multiple = false, allowOther = false, value, other, onChange, onOtherChange,
	error, required = false, onBlur, msg,
}) {
	const all = allowOther ? [...options, { value: OTHER_SENTINEL, label: msg("widget.other", "Other") }] : options;
	const selected = multiple ? (Array.isArray(value) ? value : []) : (value ?? "");
	const otherChosen = multiple ? selected.includes(OTHER_SENTINEL) : selected === OTHER_SENTINEL;
	const errId = error ? `${id}-error` : undefined;
	const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

	const option = (o, i) => {
		const descId = o.description ? `${id}-d${i}` : undefined;
		const inputProps = descId ? { "aria-describedby": descId } : {};
		const control = multiple
			? <Checkbox size="small" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} inputProps={inputProps} />
			: <Radio size="small" inputProps={inputProps} />;
		return (
			<Box key={o.value} data-testid="widget-option" sx={{ ...OPTION_SX, display: "flex", alignItems: "center", gap: 1 }}>
				{o.imageUrl && (
					<Box component="img" data-testid="widget-option-image" src={o.imageUrl} alt={o.label} sx={{ width: 34, height: 34, borderRadius: "6px", objectFit: "cover", flexShrink: 0 }} />
				)}
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<FormControlLabel value={o.value} control={control} label={o.label} sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12.5 } }} />
					{o.description && <Typography id={descId} sx={{ fontSize: 11.5, pl: 3.75, pb: 0.5 }}>{o.description}</Typography>}
				</Box>
			</Box>
		);
	};

	return (
		<FormControl component="fieldset" error={!!error} required={required} fullWidth onBlur={onBlur} aria-describedby={errId}>
			<FormLabel component="legend" sx={LEGEND_SX}>{legend}</FormLabel>
			{subtitle && <Typography sx={{ fontSize: 11.5, mb: 0.75 }}>{subtitle}</Typography>}
			{multiple
				? <FormGroup sx={{ gap: 0.75 }}>{all.map(option)}</FormGroup>
				: (
					<RadioGroup name={`${id}-choice`} value={selected} onChange={(e) => onChange(e.target.value)} sx={{ gap: 0.75 }}>
						{all.map(option)}
					</RadioGroup>
				)}
			{otherChosen && (
				<MuiTextField
					id={`${id}-other`}
					data-testid="widget-other"
					autoFocus
					size="small"
					fullWidth
					label={msg("widget.other_input", "Your answer")}
					placeholder={msg("widget.other_placeholder", "Type your answer")}
					value={other ?? ""}
					onChange={(e) => onOtherChange(e.target.value)}
					sx={{ ...FIELD_SX, mt: 1 }}
				/>
			)}
			{error && <FormHelperText id={errId} sx={{ mx: 0, color: "var(--w-error)" }}>{error}</FormHelperText>}
		</FormControl>
	);
}
